import { useState, useEffect } from "react";
import { Loader2, Plus, Server, Trash2, Pencil, Wallet, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", api_url: "", api_key: "", active: true };

const Providers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [balances, setBalances] = useState({});

  const load = () => { setLoading(true); api.get("/admin/providers").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setOpen(true); };
  const openEdit = (p) => { setForm({ name: p.name, api_url: p.api_url, api_key: "", active: p.active }); setEditing(p.id); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.api_url) return toast.error("Completá nombre y URL del API");
    try {
      if (editing === "new") await api.post("/admin/providers", form);
      else await api.put(`/admin/providers/${editing}`, form);
      toast.success("Proveedor guardado");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => { if (!window.confirm("¿Eliminar proveedor?")) return; await api.delete(`/admin/providers/${id}`); load(); };

  const checkBalance = async (id) => {
    setBalances((b) => ({ ...b, [id]: "..." }));
    try {
      const { data } = await api.get(`/admin/providers/${id}/balance`);
      setBalances((b) => ({ ...b, [id]: `${data.balance ?? "?"} ${data.currency ?? ""}` }));
    } catch (e) { setBalances((b) => ({ ...b, [id]: "Error" })); toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Proveedores (Entrega automática)" subtitle="Conectá tu panel SMM (API estándar v2) para entregar pedidos automáticamente"
        action={<Button onClick={openNew} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="new-provider-btn"><Plus className="h-4 w-4 mr-1" /> Nuevo proveedor</Button>} />

      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-5 text-sm text-indigo-800">
        Compatible con el API estándar SMM v2 (endpoint único con <code className="bg-white px-1 rounded">key</code>, <code className="bg-white px-1 rounded">action=add</code>, <code className="bg-white px-1 rounded">service</code>, <code className="bg-white px-1 rounded">link</code>, <code className="bg-white px-1 rounded">quantity</code>). Cargá la URL y tu API key, luego mapeá cada paquete a un ID de servicio en la sección <strong>Servicios</strong>.
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        : items.length === 0 ? <EmptyState icon={Server} text="Sin proveedores. Agregá uno para habilitar la entrega automática." />
        : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((p) => (
              <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`provider-${p.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-bold text-slate-900">{p.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Activo" : "Inactivo"}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 break-all">{p.api_url}</p>
                    <p className="text-xs text-slate-400">API key: {p.api_key_hint || "no configurada"}</p>
                    {balances[p.id] && <p className="text-xs text-indigo-600 mt-1">Saldo: {balances[p.id]}</p>}
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => checkBalance(p.id)} data-testid={`balance-${p.id}`}><Wallet className="h-4 w-4 mr-1" /> Saldo</Button>
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="outline" size="sm" className="rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => del(p.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing === "new" ? "Nuevo proveedor" : "Editar proveedor"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg mt-1" placeholder="Mi Panel SMM" data-testid="provider-name" /></div>
            <div><Label>URL del API</Label><Input value={form.api_url} onChange={(e) => setForm({ ...form, api_url: e.target.value })} className="rounded-lg mt-1" placeholder="https://tupanel.com/api/v2" data-testid="provider-url" /></div>
            <div><Label>API key {editing !== "new" && <span className="text-slate-400 text-xs">(dejar vacío para mantener)</span>}</Label><Input value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} className="rounded-lg mt-1" placeholder="tu-api-key" data-testid="provider-key" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Activo</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancelar</Button>
            <Button onClick={save} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-provider">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Providers;
